import React, { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { X } from "lucide-react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useTimelineImageUpload } from "@/hooks/useTimelineImageUpload";
import { useTranslation } from "react-i18next";
import { useMobileDetect } from "@/hooks/useMobileDetect";
import { PrivacyLevel, TimelineSection } from "@/types";
import { getSectionDisplayMessage } from "@/utils/timelineSectionHelpers";

import ChatMessage from "./ChatMessage";
import QuickReplyButtons from "./QuickReplyButtons";
import ChatInput from "./ChatInput";
import RollingDatePicker from "./RollingDatePicker";
import PhotoUploadChatWrapper from "./PhotoUploadChatWrapper";

interface AddMemoryChatModalProps {
  isOpen: boolean;
  onClose: (createdSectionId?: string) => void;
  targetUserId?: string;
  targetUserProfile?: any;
  timelineSections?: TimelineSection[];
  sectionsLoading?: boolean;
  timelineError?: boolean;
  preselectedSectionId?: string;
}

type ConversationStep = 
  | 'title'
  | 'lifePhase'
  | 'date'
  | 'dateSpecific'
  | 'dateUncertain'
  | 'photos'
  | 'description'
  | 'privacy'
  | 'confirm';

interface Message {
  id: string;
  type: 'bot' | 'user';
  content: string;
  timestamp: Date;
}

interface MemoryData {
  title: string;
  content: string;
  photos: File[];
  uploadedPhotoUrls: string[];
  privacy: PrivacyLevel;
  sectionId: string;
  year: string;
  month: string;
  day: string;
  season?: string;
}

const AddMemoryChatModal = ({ 
  isOpen, 
  onClose, 
  targetUserId, 
  targetUserProfile,
  timelineSections = [],
  sectionsLoading = false,
  timelineError = false,
  preselectedSectionId
}: AddMemoryChatModalProps) => {
  const { user } = useAuth();
  const { t } = useTranslation('memory');
  const { uploadImages, isUploading } = useTimelineImageUpload(targetUserId || '');
  const { isMobileOrTablet } = useMobileDetect();
  
  const getInitialStep = (): ConversationStep => {
    return preselectedSectionId ? 'title' : 'title';
  };
  
  const [currentStep, setCurrentStep] = useState<ConversationStep>(getInitialStep());
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);
  const [memoryData, setMemoryData] = useState<MemoryData>({
    title: '',
    content: '',
    photos: [],
    uploadedPhotoUrls: [],
    privacy: PrivacyLevel.FRIENDS,
    sectionId: preselectedSectionId || '',
    year: '',
    month: '',
    day: '',
  });
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  // Debug logging
  useEffect(() => {
    console.log('🎯 AddMemoryChatModal - Props changed:', {
      isOpen,
      preselectedSectionId,
      sectionsLoading,
      timelineError,
      timelineSectionsLength: timelineSections.length,
      targetUserProfile
    });
  }, [isOpen, preselectedSectionId, sectionsLoading, timelineError, timelineSections.length]);

  useEffect(() => {
    if (isOpen && isMobileOrTablet()) {
      // Prevent background scroll on mobile
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      
      return () => {
        document.body.style.overflow = '';
        document.documentElement.style.overflow = '';
      };
    }
  }, [isOpen, isMobileOrTablet]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Fixed initialization logic
  useEffect(() => {
    console.log('🚀 AddMemoryChatModal - Initialization effect triggered:', {
      isOpen,
      messagesLength: messages.length,
      sectionsLoading,
      timelineError,
      preselectedSectionId,
      isInitialized,
      timelineSectionsLength: timelineSections.length
    });

    if (isOpen && !isInitialized && !sectionsLoading && !timelineError && timelineSections.length > 0) {
      console.log('🎬 AddMemoryChatModal - Starting initialization...');
      
      // Initialize conversation
      addBotMessage(t('chat.welcome'));
      
      if (preselectedSectionId) {
        // Find the preselected section
        const preselectedSection = timelineSections.find(s => s.id === preselectedSectionId);
        console.log('🎯 AddMemoryChatModal - Preselected section found:', preselectedSection?.name);
        
        if (preselectedSection) {
          // Set the section in memory data
          setMemoryData(prev => ({ ...prev, sectionId: preselectedSectionId }));
          
          // Skip directly to title question
          setTimeout(() => {
            addBotMessage(t('chat.askTitle'));
            setCurrentStep('title');
          }, 500);
        } else {
          // Fallback if section not found
          setTimeout(() => {
            addBotMessage(t('chat.askTitle'));
            setCurrentStep('title');
          }, 500);
        }
      } else {
        // Normal flow - start with title
        setTimeout(() => {
          addBotMessage(t('chat.askTitle'));
          setCurrentStep('title');
        }, 500);
      }
      
      setIsInitialized(true);
    }
  }, [isOpen, sectionsLoading, timelineError, preselectedSectionId, timelineSections, isInitialized, messages.length]);

  const addBotMessage = (content: string) => {
    const message: Message = {
      id: Date.now().toString(),
      type: 'bot',
      content,
      timestamp: new Date(),
    };
    console.log('🤖 AddMemoryChatModal - Adding bot message:', content);
    setMessages(prev => [...prev, message]);
  };

  const addUserMessage = (content: string) => {
    const message: Message = {
      id: Date.now().toString(),
      type: 'user',
      content,
      timestamp: new Date(),
    };
    console.log('👤 AddMemoryChatModal - Adding user message:', content);
    setMessages(prev => [...prev, message]);
  };

  const handleStepProgress = async (stepData: any) => {
    console.log('⚡ AddMemoryChatModal - Step progress:', { currentStep, stepData });
    
    switch (currentStep) {
      case 'title':
        setMemoryData(prev => ({ ...prev, title: stepData }));
        addUserMessage(stepData);
        
        // Skip life phase if section is preselected
        if (preselectedSectionId) {
          console.log('⏭️ AddMemoryChatModal - Skipping life phase (preselected)');
          setTimeout(() => {
            addBotMessage(t('chat.askDate'));
            setCurrentStep('date');
          }, 500);
        } else {
          setTimeout(() => {
            addBotMessage(t('chat.askLifePhase'));
            setCurrentStep('lifePhase');
          }, 500);
        }
        break;

      case 'lifePhase':
        // This should only run if no section is preselected
        if (!preselectedSectionId) {
          setMemoryData(prev => ({ ...prev, sectionId: stepData.id }));
          addUserMessage(stepData.name);
          setTimeout(() => {
            addBotMessage(t('chat.askDate'));
            setCurrentStep('date');
          }, 500);
        }
        break;

      case 'date':
        addUserMessage(stepData);
        if (stepData === t('chat.specificDate')) {
          setTimeout(() => {
            addBotMessage(t('chat.selectSpecificDate'));
            setCurrentStep('dateSpecific');
          }, 500);
        } else {
          setTimeout(() => {
            addBotMessage(t('chat.selectUncertainDate'));
            setCurrentStep('dateUncertain');
          }, 500);
        }
        break;

      case 'dateSpecific':
      case 'dateUncertain':
        setMemoryData(prev => ({
          ...prev,
          year: stepData.year || '',
          month: stepData.month || '',
          day: stepData.day || '',
          season: stepData.season,
        }));
        addUserMessage(stepData.label);
        setTimeout(() => {
          addBotMessage(t('chat.askPhotos'));
          setCurrentStep('photos');
        }, 500);
        break;

      case 'photos':
        if (stepData.photos && stepData.photos.length > 0) {
          setMemoryData(prev => ({ ...prev, photos: stepData.photos }));
          addUserMessage(t('chat.photosAdded', { count: stepData.photos.length }));
        } else {
          addUserMessage(t('chat.noPhotos'));
        }
        setTimeout(() => {
          addBotMessage(t('chat.askDescription'));
          setCurrentStep('description');
        }, 500);
        break;

      case 'description':
        setMemoryData(prev => ({ ...prev, content: stepData || '' }));
        addUserMessage(stepData || t('chat.skipDescription'));
        setTimeout(() => {
          addBotMessage(t('chat.askPrivacy'));
          setCurrentStep('privacy');
        }, 500);
        break;

      case 'privacy':
        setMemoryData(prev => ({ ...prev, privacy: stepData.value }));
        addUserMessage(stepData.label);
        setTimeout(() => {
          addBotMessage(t('chat.readyToSave'));
          setCurrentStep('confirm');
        }, 500);
        break;
    }
  };

  const handleTextSubmit = () => {
    if (!inputValue.trim()) return;

    if (currentStep === 'title') {
      handleStepProgress(inputValue);
    } else if (currentStep === 'description') {
      handleStepProgress(inputValue);
    }
    
    setInputValue('');
  };

  const handleConfirm = async () => {
    if (!user) {
      toast.error(t('messages.loginRequired'));
      return;
    }
    
    if (!memoryData.title.trim() || !memoryData.sectionId) {
      toast.error(t('messages.fillRequired'));
      return;
    }
    
    if (!targetUserId) {
      toast.error('Target user not found');
      return;
    }
    
    try {
      // Upload images first if any
      if (memoryData.photos.length > 0) {
        const uploadedUrls = await uploadImages(memoryData.photos);
        setMemoryData(prev => ({ ...prev, uploadedPhotoUrls: uploadedUrls }));
      }
      
      let dateObj = null;
      let formattedDate = null;
      let yearNum = memoryData.year ? parseInt(memoryData.year) : null;
      let monthNum = memoryData.month ? parseInt(memoryData.month) : null;
      let dayNum = memoryData.day ? parseInt(memoryData.day) : null;
      
      if (yearNum) {
        monthNum = monthNum || 1;
        dayNum = dayNum || 1;
        dateObj = new Date(yearNum, monthNum - 1, dayNum);
        formattedDate = format(dateObj, "yyyy-MM-dd");
      }
      
      const { error } = await supabase
        .from('profile_timeline_entries')
        .insert({
          user_id: targetUserId, // Timeline owner's ID
          creator_id: user.id, // Current user's ID
          profile_timeline_section_id: memoryData.sectionId,
          title: memoryData.title,
          content: memoryData.content || null,
          date: formattedDate,
          year: yearNum,
          month: monthNum,
          day: dayNum,
          privacy_level: memoryData.privacy,
          photos: memoryData.uploadedPhotoUrls.length > 0 ? memoryData.uploadedPhotoUrls : null
        });
        
      if (error) throw error;
      
      toast.success(t('messages.memoryCreated'));
      onClose(memoryData.sectionId);
    } catch (error: any) {
      toast.error(t('messages.memoryError', { error: error.message }));
    }
  };

  const resetChat = () => {
    console.log('🔄 AddMemoryChatModal - Resetting chat');
    setCurrentStep(preselectedSectionId ? 'title' : 'title');
    setMessages([]);
    setInputValue('');
    setIsInitialized(false);
    setMemoryData({
      title: '',
      content: '',
      photos: [],
      uploadedPhotoUrls: [],
      privacy: PrivacyLevel.FRIENDS,
      sectionId: preselectedSectionId || '',
      year: '',
      month: '',
      day: '',
    });
  };

  const handleClose = () => {
    resetChat();
    onClose();
  };

  // Enhanced handler for section changes due to year selection
  const handleSectionChange = (newSection: TimelineSection) => {
    console.log('🔄 AddMemoryChatModal - Section changed due to year selection:', {
      oldSectionId: memoryData.sectionId,
      newSectionId: newSection.id,
      newSectionName: newSection.name
    });

    const oldSection = timelineSections.find(s => s.id === memoryData.sectionId);
    
    // Update memory data with new section
    setMemoryData(prev => ({ ...prev, sectionId: newSection.id }));
    
    // Add bot message about the section change
    const changeMessage = getSectionDisplayMessage(oldSection, newSection, t);
    if (changeMessage) {
      console.log('🔄 Adding section change message:', changeMessage);
      setTimeout(() => {
        addBotMessage(changeMessage);
      }, 300);
    }
  };

  const renderCurrentInput = () => {
    // Show loading state while sections are being loaded
    if (sectionsLoading) {
      return (
        <div className="flex justify-center p-4">
          <div className="text-gray-500">Loading timeline sections...</div>
        </div>
      );
    }

    // Show error state if timeline data couldn't be loaded
    if (timelineError) {
      return (
        <div className="flex flex-col items-center p-4 text-center">
          <div className="text-red-500 mb-2">Unable to load timeline data</div>
          <button
            onClick={handleClose}
            className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-full"
          >
            Close
          </button>
        </div>
      );
    }

    switch (currentStep) {
      case 'title':
      case 'description':
        return (
          <ChatInput
            value={inputValue}
            onChange={setInputValue}
            onSubmit={handleTextSubmit}
            placeholder={currentStep === 'title' ? t('chat.titlePlaceholder') : t('chat.descriptionPlaceholder')}
          />
        );

      case 'lifePhase':
        // Skip this step if section is preselected
        if (preselectedSectionId) {
          return null;
        }
        return (
          <QuickReplyButtons
            options={timelineSections?.map(section => ({
              id: section.id,
              label: section.name,
              value: section,
              ageRange: section.age_from !== undefined && section.age_to !== undefined ? `${section.age_from}-${section.age_to} Jahre` : undefined
            })) || []}
            onSelect={handleStepProgress}
          />
        );

      case 'date':
        return (
          <RollingDatePicker
            onSelect={handleStepProgress}
            onSectionChange={handleSectionChange}
            showToday={true}
            timelineSections={timelineSections}
            currentSectionId={memoryData.sectionId}
            userBirthdate={targetUserProfile?.birthdate}
          />
        );

      case 'dateSpecific':
        return (
          <RollingDatePicker
            onSelect={handleStepProgress}
            onSectionChange={handleSectionChange}
            showToday={true}
            timelineSections={timelineSections}
            currentSectionId={memoryData.sectionId}
            userBirthdate={targetUserProfile?.birthdate}
          />
        );

      case 'dateUncertain':
        return (
          <RollingDatePicker
            onSelect={handleStepProgress}
            onSectionChange={handleSectionChange}
            showToday={false}
            timelineSections={timelineSections}
            currentSectionId={memoryData.sectionId}
            userBirthdate={targetUserProfile?.birthdate}
          />
        );

      case 'photos':
        return (
          <PhotoUploadChatWrapper
            onPhotosSelected={handleStepProgress}
          />
        );

      case 'privacy':
        return (
          <QuickReplyButtons
            options={[
              { 
                id: 'private', 
                label: t('visibility.private.title'), 
                value: { value: PrivacyLevel.PRIVATE, label: t('visibility.private.title') }
              },
              { 
                id: 'friends', 
                label: t('visibility.friends.title'), 
                value: { value: PrivacyLevel.FRIENDS, label: t('visibility.friends.title') }
              },
              { 
                id: 'public', 
                label: t('visibility.public.title'), 
                value: { value: PrivacyLevel.PUBLIC, label: t('visibility.public.title') }
              }
            ]}
            onSelect={handleStepProgress}
          />
        );

      case 'confirm':
        return (
          <div className="flex justify-center pt-4">
            <button
              onClick={handleConfirm}
              disabled={isUploading}
              className="bg-uplivion-blue hover:bg-blue-700 text-white px-8 py-3 rounded-full font-medium transition-colors disabled:opacity-50"
            >
             Test {isUploading ? t('buttons.saving') : t('summary.submitButton')}
            </button>
          </div>
        );

      default:
        return null;
    }
  };

  // Don't render modal if timeline is still loading or has error and no messages yet
  if ((sectionsLoading || timelineError) && messages.length === 0) {
    return null;
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Mobile backdrop */}
          {isMobileOrTablet() && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/20 backdrop-blur-sm z-20"
              onClick={handleClose}
            />
          )}
          
          <motion.div
            initial={{ y: isMobileOrTablet() ? "100%" : "100%" }}
            animate={{ y: 0 }}
            exit={{ y: isMobileOrTablet() ? "100%" : "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className={`
              fixed z-30 flex justify-center
              ${isMobileOrTablet() 
                ? 'inset-0 px-0' 
                : 'left-0 right-0 bottom-[88px] px-4'
              }
            `}
          >
            <div
              className={`
                bg-white/80 backdrop-blur-xl border border-white/20 shadow-2xl overflow-hidden
                ${isMobileOrTablet() 
                  ? 'w-full h-full rounded-none' 
                  : 'w-full max-w-[450px] rounded-t-3xl'
                }
              `}
              style={!isMobileOrTablet() ? { maxHeight: 'calc(100vh - 150px)' } : {}}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-white/20">
                <h2 className="text-lg font-semibold text-gray-900">
                  {t('modal.title')}
                </h2>
                <button
                  onClick={handleClose}
                  className="p-2 hover:bg-white/20 rounded-full transition-colors"
                >
                  <X className="h-5 w-5 text-gray-600" />
                </button>
              </div>

              {/* Chat Messages */}
              <div
                ref={chatContainerRef}
                className={`
                  flex flex-col overflow-y-auto p-4 space-y-4
                  ${isMobileOrTablet() 
                    ? 'flex-1' 
                    : 'h-96'
                  }
                `}
                style={{
                  // Prevent scroll propagation to background
                  touchAction: 'pan-y',
                }}
                onTouchMove={(e) => {
                  // Only prevent if at scroll boundaries
                  const element = e.currentTarget;
                  const { scrollTop, scrollHeight, clientHeight } = element;
                  
                  if (scrollTop === 0 && e.touches[0].clientY > e.touches[0].pageY) {
                    // Scrolling up at top
                    e.preventDefault();
                  } else if (scrollTop + clientHeight >= scrollHeight && e.touches[0].clientY < e.touches[0].pageY) {
                    // Scrolling down at bottom
                    e.preventDefault();
                  }
                }}
              >
                {messages.map((message) => (
                  <ChatMessage
                    key={message.id}
                    message={message}
                  />
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Input Area */}
              <div className={`
                border-t border-white/20 bg-white/40
                ${isMobileOrTablet() ? 'p-4 pb-8' : 'p-4'}
              `}>
                {renderCurrentInput()}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default AddMemoryChatModal;
